/**
 * Outbox local en IndexedDB para offline-first.
 *
 * Patrón:
 * 1. Cualquier acción del auxiliar/cocinera se guarda PRIMERO en outbox local.
 * 2. Si hay conexión: se intenta enviar al servidor inmediatamente.
 * 3. Si falla o no hay conexión: queda con sync_status='PENDING'.
 * 4. El SyncManager (sync.ts) reintenta cuando el navegador detecta online.
 *
 * Idempotencia: cada evento tiene un UUID v4 único. El backend ignora duplicados.
 *
 * Sprint 2 Tarea 2.6 — agrega tabla qrs_cache para validar QRs offline.
 */
import Dexie, { Table } from 'dexie'

export interface OutboxEvento {
  uuid: string
  comida_elegida_id: number
  tipo_evento: string
  payload?: any
  qr_token?: string
  timestamp_local: string
  sync_status: 'PENDING' | 'SYNCED' | 'FAILED'
  retry_count: number
  last_error?: string
}

export interface CachedComida {
  id: number
  uuid: string
  cliente_nombre?: string
  habitacion?: string
  dieta_nombre?: string
  tiempo_nombre?: string
  estado: string
  lineas: any[]
  notas_paciente?: string
  cached_at: string
}

export interface CachedQr {
  token: string
  habitacion: string
  activo: boolean
  cached_at: string
}

class AvanteDB extends Dexie {
  outbox!: Table<OutboxEvento, string>
  comidas_cache!: Table<CachedComida, number>
  qrs_cache!: Table<CachedQr, string>

  constructor() {
    super('avante_dietas')
    this.version(1).stores({
      outbox: 'uuid, sync_status, comida_elegida_id, timestamp_local',
      comidas_cache: 'id, estado, cached_at',
    })
    this.version(2).stores({
      outbox: 'uuid, sync_status, comida_elegida_id, timestamp_local',
      comidas_cache: 'id, estado, cached_at',
      qrs_cache: '&token, habitacion, activo, cached_at',
    })
  }
}

export const db = new AvanteDB()

// UUID v4 generator (sin depender de crypto.randomUUID en navegadores viejos)
export function uuidv4(): string {
  if (crypto.randomUUID) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/**
 * Encolar un evento (offline-safe).
 * Retorna el UUID generado.
 */
export async function enqueueEvento(
  comida_elegida_id: number,
  tipo_evento: string,
  payload?: any,
  qr_token?: string,
): Promise<string> {
  const uuid = uuidv4()
  const evento: OutboxEvento = {
    uuid,
    comida_elegida_id,
    tipo_evento,
    payload,
    qr_token,
    timestamp_local: new Date().toISOString(),
    sync_status: 'PENDING',
    retry_count: 0,
  }
  await db.outbox.add(evento)
  return uuid
}

// ============================================================
// QR cache (Sprint 2 Tarea 2.6.5) — validación offline + 3 layers
// ============================================================
export const QR_CACHE_TTL_MS = 24 * 60 * 60 * 1000

export async function findQrLocal(token: string): Promise<CachedQr | null> {
  const entry = await db.qrs_cache.where('token').equals(token).first()
  return entry || null
}

export function isQrCacheExpired(entry: CachedQr): boolean {
  return Date.now() - new Date(entry.cached_at).getTime() > QR_CACHE_TTL_MS
}

export async function bulkReplaceQrsCache(
  qrs: Array<{ token: string; habitacion: string; activo: boolean }>,
): Promise<void> {
  const now = new Date().toISOString()
  await db.qrs_cache.clear()
  await db.qrs_cache.bulkAdd(qrs.map((q) => ({ ...q, cached_at: now })))
}

export async function clearQrsCache(): Promise<void> {
  await db.qrs_cache.clear()
}

export async function deleteQrFromCache(token: string): Promise<void> {
  await db.qrs_cache.where('token').equals(token).delete()
}
